import { AlertDialog as BaseAlertDialog } from '@base-ui/react/alert-dialog';
import type { ComponentProps, ReactNode } from 'react';
import { LAYER } from '../lib/layer.ts';
import { cx } from '../lib/styled.tsx';

export interface AlertDialogProps extends ComponentProps<typeof BaseAlertDialog.Root> {
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * A {@link Dialog} that cannot be dismissed by clicking past it.
 *
 * For destructive or irreversible confirmations, where closing the dialog by
 * accident would read as an answer. Escape still closes it — screen reader and
 * keyboard users are entitled to that way out — so the decision must be safe to
 * defer, and every alert dialog needs an explicit way out among its children
 * ({@link AlertDialogClose}).
 */
export function AlertDialog({
  trigger,
  title,
  description,
  children,
  className,
  ...rootProps
}: AlertDialogProps) {
  return (
    <BaseAlertDialog.Root {...rootProps}>
      {trigger ? <BaseAlertDialog.Trigger render={trigger as never} /> : null}
      <BaseAlertDialog.Portal>
        <BaseAlertDialog.Backdrop
          className={cx(
            `${LAYER.dialog} fixed inset-0 bg-scrim backdrop-blur-[4px] transition-opacity duration-200`,
            'data-starting-style:opacity-0 data-ending-style:opacity-0',
          )}
        />
        <BaseAlertDialog.Popup
          className={cx(
            `${LAYER.dialog} fixed top-1/2 left-1/2 w-[min(26rem,calc(100vw-2rem))] -translate-1/2`,
            'rounded-modal bg-surface-raised px-8 py-6 text-ink shadow-modal',
            'transition-[opacity,scale] duration-200 ease-out-quint outline-none',
            'data-starting-style:scale-95 data-starting-style:opacity-0',
            'data-ending-style:scale-95 data-ending-style:opacity-0',
            className,
          )}
        >
          <BaseAlertDialog.Title className="font-display text-lg font-bold tracking-tight">
            {title}
          </BaseAlertDialog.Title>
          {description ? (
            <BaseAlertDialog.Description className="mt-2 text-bodysm leading-relaxed text-ink-muted">
              {description}
            </BaseAlertDialog.Description>
          ) : null}
          <div className="mt-5">{children}</div>
        </BaseAlertDialog.Popup>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  );
}

export const AlertDialogClose = BaseAlertDialog.Close;
